// src/components/Modals.tsx (обновлённый код)
import React, { useState, useEffect } from 'react';
import { useModal } from '../context/ModalContext';
import { paymentOptions, PaymentOption } from '../config/paymentOptions';
import { fetchModelSettings, saveModelSettings, createPayment } from '../api';
import styles from './ChatApp.module.css';

const Modals: React.FC = () => {
  const { activeModal, isOverlayVisible, closeModal } = useModal();
  const [paymentStep, setPaymentStep] = useState(1);
  
  // Выбираем среднюю сумму из paymentOptions
  const defaultAmountIndex = Math.floor(paymentOptions.length / 2);
  const defaultAmount = paymentOptions[defaultAmountIndex].amount;
  
  const [amount, setAmount] = useState<number>(defaultAmount);
  const [bonus, setBonus] = useState<number>(0);
  const [activeButton, setActiveButton] = useState<string | null>(String(defaultAmount));
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('yookassa'); // По умолчанию ЮKassa
  
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2000);
  const [topP, setTopP] = useState(0.9);
  const [systemPrompt, setSystemPrompt] = useState('Вы полезный AI-ассистент');
  const [tokenConversion, setTokenConversion] = useState('Примерно 1500 слов (3 страницы)');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await fetchModelSettings();
        setTemperature(settings.temperature || 0.7);
        setMaxTokens(settings.maxTokens || 2000);
        setTopP(settings.topP || 0.9);
        setSystemPrompt(settings.systemPrompt || 'Вы полезный AI-ассистент');
        updateTokenConversion(settings.maxTokens || 2000);
      } catch (error) {
        console.error('Ошибка загрузки настроек:', error);
        updateTokenConversion(2000);
      }
    };
    loadSettings();
    
    // Устанавливаем бонус для средней суммы при инициализации
    const { bonus } = calculateBonusAndActive(defaultAmount);
    setBonus(bonus);
  }, []);

  const updateSliderValue = (value: number | string, type: string) => {
    const parsedValue = typeof value === 'string' ? parseFloat(value) : value;
    if (type === 'temperature') setTemperature(parsedValue);
    else if (type === 'maxTokens') {
      setMaxTokens(parsedValue);
      updateTokenConversion(parsedValue);
    } else if (type === 'topP') setTopP(parsedValue);
  };

  const updateTokenConversion = (value: number) => {
    if (value >= 8100) setTokenConversion('Без ограничений');
    else {
      const words = Math.round(value * 0.75);
      const pages = (words / 500).toFixed(1);
      setTokenConversion(`Примерно ${words} слов (${pages} страниц)`);
    }
  };

  const resetToDefaults = () => {
    setTemperature(0.7);
    setMaxTokens(2000);
    setTopP(0.9);
    setSystemPrompt('Вы полезный AI-ассистент');
    updateTokenConversion(2000);
  };

  const saveModelSettingsHandler = async () => {
    const settings = {
      temperature,
      maxTokens: maxTokens >= 8192 ? 0 : maxTokens,
      topP,
      systemPrompt,
    };
    try {
      await saveModelSettings(settings);
      console.log('Настройки сохранены на сервере');
      closeModal();
    } catch (error) {
      console.error('Ошибка при сохранении настроек:', error);
    }
  };

  const nextStep = () => setPaymentStep(2);
  const prevStep = () => setPaymentStep(1);

  const calculateBonusAndActive = (amt: number): { bonus: number; activeAmount: string | null } => {
    const applicableOption = paymentOptions
      .filter((option) => amt >= option.amount)
      .sort((a, b) => b.amount - a.amount)[0] || paymentOptions[0];
    const bonus = applicableOption
      ? Math.round((applicableOption.amount * applicableOption.bonusPercentage) / 100)
      : 0;
    const activeAmount = applicableOption && amt >= applicableOption.amount ? String(applicableOption.amount) : null;
    return { bonus, activeAmount };
  };

  const setPaymentAmount = (amt: number) => {
    setAmount(amt);
    const { bonus, activeAmount } = calculateBonusAndActive(amt);
    setBonus(bonus);
    setActiveButton(activeAmount);
  };

  const handleAmountInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setAmount(value);
    const { bonus, activeAmount } = calculateBonusAndActive(value);
    setBonus(bonus);
    setActiveButton(activeAmount);
  };

  const processPayment = async () => {
    if (amount < 100) {
      alert('Минимальная сумма 100 рублей!');
      return;
    }
    try {
      const response = await createPayment(amount);
      if (response.confirmation_url) {
        window.location.href = response.confirmation_url;  // Перенаправление на страницу оплаты YooKassa
      } else {
        alert('Ошибка: не получена ссылка на оплату');
      }
    } catch (error) {
      console.error('Ошибка создания платежа:', error);
      alert('Ошибка при создании платежа. Попробуйте позже.');
    }
    closeModal();
  };

  const handlePaymentMethodSelect = (method: string) => {
    setSelectedPaymentMethod(method);
  };

  const paymentMethods = [
    {
      id: 'yookassa',
      title: 'ЮKassa',
      description: 'Visa, Mastercard, Apple/Google Pay, МИР',
      icons: ['fab fa-cc-visa', 'fab fa-cc-mastercard', 'fab fa-apple-pay'],
    },
  ];

  return (
    <div
      id="modalOverlay"
      className={`${styles.modalOverlay} ${!isOverlayVisible ? styles.modalOverlayHidden : ''}`}
      onClick={closeModal}
    >
      {/* Subscription Modal */}
      <div
        id="subscriptionsModal"
        className={`${styles.subscriptionModal}`}
        style={{ display: activeModal === 'subscriptionsModal' ? 'block' : 'none' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.modalTitle}>Тарифные планы</h2>
        <button className={styles.closeBtn} onClick={closeModal}>
          <i className="fas fa-times"></i>
        </button>
        <div className={styles.plansGrid}>{/* Планы подписки */}</div>
      </div>

      {/* Model Settings Modal */}
      <div
        id="modelSettingsModal"
        className={`${styles.modelSettingsModal}`}
        style={{ display: activeModal === 'modelSettingsModal' ? 'block' : 'none' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.modalTitle}>Настройки модели</h2>
        <button className={styles.closeBtn} onClick={closeModal}>
          <i className="fas fa-times"></i>
        </button>
        <div className={styles.plansGrid}>
          <div className={styles.settingGroup}>
            <div className={styles.settingHeader}>
              <div className={styles.settingTitle}>
                <span>Температура</span>
                <div className={styles.settingTooltip}>
                  <i className="fas fa-info-circle"></i>
                  <span className={styles.tooltipText}>
                    Управляет случайностью ответов:<br />0 = строгий и предсказуемый<br />2 = креативный и случайный
                  </span>
                </div>
              </div>
              <span className={styles.redBadge}>{temperature.toFixed(2)}</span>
            </div>
            <div>
              <input
                type="range"
                min="0"
                max="2"
                step="0.05"
                value={temperature}
                className={styles.settingsSlider}
                onChange={(e) => updateSliderValue(e.target.value, 'temperature')}
              />
              <div className={styles.sliderScale}>
                <span className={styles.scaleMark} style={{ left: '0%' }} onClick={() => updateSliderValue(0, 'temperature')}>
                  0
                </span>
                <span
                  className={`${styles.scaleMark} ${styles.recommendedRange}`}
                  style={{ left: '35%' }}
                  onClick={() => updateSliderValue(0.7, 'temperature')}
                >
                  0.7
                </span>
                <span
                  className={`${styles.scaleMark} ${styles.recommendedRange}`}
                  style={{ left: '50%' }}
                  onClick={() => updateSliderValue(1.0, 'temperature')}
                >
                  1.0
                </span>
                <span
                  className={styles.scaleMark}
                  style={{ left: '100%' }}
                  onClick={() => updateSliderValue(2, 'temperature')}
                >
                  2
                </span>
              </div>
            </div>
            <p className={styles.settingDescription}>Рекомендуем 0.7-1.0 для баланса креатива и точности</p>
          </div>

          <div className={styles.settingGroup}>
            <div className={styles.settingHeader}>
              <div className={styles.settingTitle}>
                <span>Лимит ответа</span>
                <div className={styles.settingTooltip}>
                  <i className="fas fa-info-circle"></i>
                  <span className={styles.tooltipText}>Максимальное количество токенов (8192 = без ограничений)</span>
                </div>
              </div>
              <span className={styles.redBadge}>{maxTokens >= 8100 ? '∞' : maxTokens}</span>
            </div>
            <div>
              <input
                type="range"
                min="500"
                max="8100"
                step="100"
                value={maxTokens}
                className={styles.settingsSlider}
                onChange={(e) => updateSliderValue(parseInt(e.target.value), 'maxTokens')}
              />
              <div className={styles.sliderScale}>
                <span className={styles.scaleMark} style={{ left: '0%' }} onClick={() => updateSliderValue(500, 'maxTokens')}>
                  500
                </span>
                <span
                  className={`${styles.scaleMark} ${styles.recommendedRange}`}
                  style={{ left: '19.5%' }}
                  onClick={() => updateSliderValue(2000, 'maxTokens')}
                >
                  2000
                </span>
                <span
                  className={`${styles.scaleMark} ${styles.recommendedRange}`}
                  style={{ left: '58.5%' }}
                  onClick={() => updateSliderValue(5000, 'maxTokens')}
                >
                  5000
                </span>
                <span
                  className={styles.scaleMark}
                  style={{ left: '100%' }}
                  onClick={() => updateSliderValue(8192, 'maxTokens')}
                >
                  ∞
                </span>
              </div>
            </div>
            <p className={styles.settingDescription}>
              1 токен ≈ 0.75 слова. <span>{tokenConversion}</span>
            </p>
          </div>

          <div className={styles.settingGroup}>
            <div className={styles.settingHeader}>
              <div className={styles.settingTitle}>
                <span>Разнообразие (Top-P)</span>
                <div className={styles.settingTooltip}>
                  <i className="fas fa-info-circle"></i>
                  <span className={styles.tooltipText}>
                    Фильтрация словарного запаса:<br />0 = только точные варианты<br />1 = максимальное разнообразие
                  </span>
                </div>
              </div>
              <span className={styles.redBadge}>{topP.toFixed(2)}</span>
            </div>
            <div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={topP}
                className={styles.settingsSlider}
                onChange={(e) => updateSliderValue(e.target.value, 'topP')}
              />
              <div className={styles.sliderScale}>
                <span className={styles.scaleMark} style={{ left: '0%' }} onClick={() => updateSliderValue(0, 'topP')}>
                  0
                </span>
                <span
                  className={`${styles.scaleMark} ${styles.recommendedRange}`}
                  style={{ left: '80%' }}
                  onClick={() => updateSliderValue(0.8, 'topP')}
                >
                  0.8
                </span>
                <span
                  className={styles.scaleMark}
                  style={{ left: '100%' }}
                  onClick={() => updateSliderValue(1, 'topP')}
                >
                  1
                </span>
              </div>
            </div>
            <p className={styles.settingDescription}>Используйте 0.8-1.0 для креативных задач</p>
          </div>

          <div className={styles.settingGroup}>
            <div className={styles.settingHeader}>
              <div className={styles.settingTitle}>
                <span>Роль ассистента</span>
                <div className={styles.settingTooltip}>
                  <i className="fas fa-info-circle"></i>
                  <span className={styles.tooltipText}>Опишите роль, в которой должен выступать AI</span>
                </div>
              </div>
            </div>
            <textarea
              className={styles.systemPromptInput}
              placeholder="Пример: Ты опытный программист, помогающий писать чистый код..."
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
            />
            <p className={styles.settingDescription}>Этот текст влияет на поведение модели</p>
          </div>
        </div>
        <div className={styles.modalActions}>
          <button className={`${styles.modalBtn} ${styles.cancelBtn}`} onClick={resetToDefaults}>
            <i className="fas fa-undo"></i> По умолчанию
          </button>
          <button className={`${styles.modalBtn} ${styles.saveBtn}`} onClick={saveModelSettingsHandler}>
            <i className="fas fa-save"></i> Применить
          </button>
        </div>
      </div>

      {/* Payment Modal */}
      <div
        id="paymentModal"
        className={`${styles.paymentModal}`}
        style={{ display: activeModal === 'paymentModal' ? 'block' : 'none' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className={styles.closeBtn} onClick={closeModal}>
          <i className="fas fa-times"></i>
        </button>
        <div className={`${styles.paymentStep} ${paymentStep === 1 ? styles.paymentStepActive : ''}`} id="paymentStep1">
          <h2 className={styles.modalTitle}>
            <i className="fas fa-wallet"></i> Пополнение баланса
          </h2>
          <div>
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className={`${styles.paymentMethod} ${
                  selectedPaymentMethod === method.id ? styles.paymentMethodSelected : ''
                }`}
                onClick={() => handlePaymentMethodSelect(method.id)}
              >
                <div className={styles.methodIcon}>
                  {method.icons.map((iconClass) => (
                    <i key={iconClass} className={iconClass}></i>
                  ))}
                </div>
                <div>
                  <h3>{method.title}</h3>
                  <p>{method.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.paymentNavigation}>
            <button className={`${styles.modalBtn} ${styles.cancelBtn}`} onClick={closeModal}>
              Отмена
            </button>
            <button className={`${styles.modalBtn} ${styles.nextBtn}`} onClick={nextStep}>
              Далее
            </button>
          </div>
        </div>
        <div className={`${styles.paymentStep} ${paymentStep === 2 ? styles.paymentStepActive : ''}`} id="paymentStep2">
          <h2 className={styles.modalTitle}>
            <i className="fas fa-coins"></i> Сумма пополнения
          </h2>
          <div className={styles.amountGrid}>
            {paymentOptions.map((option: PaymentOption) => (
              <button
                key={option.amount}
                className={`${styles.amountBtn} ${activeButton === String(option.amount) ? styles.amountBtnActive : ''}`}
                onClick={() => setPaymentAmount(option.amount)}
              >
                <span className={styles.amount}>{option.amount} ₽</span>
                <span className={styles.bonusAmount}>{option.bonusPercentage}% выгоды</span>
                {option.label && <span className={styles.hitLabel}>{option.label}</span>}
              </button>
            ))}
          </div>
          <div className={styles.bonusInfo} style={{ display: bonus > 0 ? 'block' : 'none' }}>
            🎁 Вы получите подарок: <b>{bonus} ₽</b>
          </div>
          <div className={styles.customAmount}>
            <input
              type="number"
              id="amountInput"
              className={styles.amountInput}
              placeholder="Введите сумму"
              value={amount || ''}
              onChange={handleAmountInput}
              min="100"
              step="50"
            />
            <span className={styles.currencySymbol}>₽</span>
          </div>
          <div className={styles.paymentNavigation}>
            <button className={`${styles.modalBtn} ${styles.backBtn}`} onClick={prevStep}>
              Назад
            </button>
            <button className={`${styles.modalBtn} ${styles.confirmBtn}`} onClick={processPayment}>
              Оплатить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modals;